import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, PhoneCall, PhoneMissed, PhoneIncoming, MessageSquare, CalendarCheck, Car, Bot } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = [{ v: 7, l: "7 days" }, { v: 30, l: "30 days" }, { v: 90, l: "90 days" }, { v: 365, l: "1 year" }];

function Stat({ label, value, icon: Icon, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 text-white/45 text-xs uppercase tracking-widest">{Icon && <Icon size={13} />}{label}</div>
      <div className="font-display font-black text-2xl mt-1.5 text-white">{value ?? "—"}</div>
      {sub && <div className="text-white/40 text-xs mt-0.5">{sub}</div>}
    </div>
  );
}

export default function AiSalesDashboard({ token }) {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    axios.get(`${API}/marketing/ai-sales?days=${days}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setD(r.data)).catch(() => {});
  }, [token, days]);

  if (!d) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Loading AI Sales…</div>;

  return (
    <div data-testid="ai-sales-dashboard" className="space-y-5">
      <div className="bg-gradient-to-r from-[#F2B705]/15 to-transparent border border-[#F2B705]/30 rounded-xl p-6 flex items-center gap-4">
        <Bot size={30} className="text-[#F2B705] shrink-0" />
        <div>
          <div data-testid="ai-revenue-headline" className="font-display font-black text-3xl text-white">AI Generated ${Number(d.revenue_month).toLocaleString()} this month</div>
          <div className="text-white/50 text-sm mt-0.5">${Number(d.revenue_total).toLocaleString()} all-time from Upfirst AI conversations · {d.rentals_closed} rentals closed</div>
        </div>
        <div className="ml-auto flex gap-1.5">
          {RANGES.map((r) => (
            <button key={r.v} onClick={() => setDays(r.v)} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${days === r.v ? "bg-[#F2B705] text-black" : "bg-white/5 border border-white/10 text-white/60"}`}>{r.l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat testid="ai-calls-today" label="AI Calls Today" value={d.calls_today} icon={PhoneCall} sub={`${d.calls_total} all-time`} />
        <Stat testid="ai-texts-today" label="AI Texts Today" value={d.texts_today} icon={MessageSquare} sub={`${d.texts_total} all-time`} />
        <Stat testid="ai-calls-answered" label="Calls Answered" value={d.calls_answered} icon={PhoneIncoming} />
        <Stat testid="ai-calls-missed" label="Calls Missed" value={d.calls_missed} icon={PhoneMissed} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat testid="ai-appointments" label="Appointments Booked" value={d.appointments_booked} icon={CalendarCheck} />
        <Stat testid="ai-rentals-closed" label="Rentals Closed" value={d.rentals_closed} icon={Car} />
        <Stat testid="ai-revenue-period" label={`Revenue (last ${d.days}d)`} value={`$${Number(d.revenue_period).toLocaleString()}`} icon={Bot} />
      </div>

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5">
        <h3 className="font-display font-bold text-white mb-3">Latest AI Conversations</h3>
        <div className="space-y-2.5">
          {d.recent.map((l) => (
            <div key={l.id} className="flex items-start gap-3 bg-black/30 border border-white/5 rounded-lg px-4 py-3">
              {(l.contact_type === "text" || l.contact_type === "sms" || l.contact_type === "chat")
                ? <MessageSquare size={16} className="text-blue-300 mt-0.5 shrink-0" />
                : l.call_outcome === "missed" ? <PhoneMissed size={16} className="text-red-400 mt-0.5 shrink-0" />
                : <PhoneCall size={16} className="text-[#F2B705] mt-0.5 shrink-0" />}
              <div className="min-w-0">
                <div className="text-white text-sm font-semibold">
                  {l.name}
                  {l.appointment && <span className="ml-2 px-1.5 py-0.5 rounded bg-green-500/15 text-green-400 text-[10px] uppercase">Appointment</span>}
                  {l.status === "booked" && <span className="ml-2 px-1.5 py-0.5 rounded bg-[#F2B705]/15 text-[#F2B705] text-[10px] uppercase">Booked</span>}
                </div>
                {l.call_summary && <div className="text-white/60 text-xs mt-0.5">{l.call_summary}{l.call_duration ? ` · ${l.call_duration}` : ""}</div>}
              </div>
              <div className="ml-auto text-white/35 text-xs whitespace-nowrap">{fmtDateTime(l.created_at)}</div>
            </div>
          ))}
          {d.recent.length === 0 && <div className="text-white/40 text-sm py-4 text-center">No AI conversations yet — connect Upfirst in the Upfirst AI tab.</div>}
        </div>
      </div>
    </div>
  );
}
